import { isEqual } from "date-fns";

import { IPickerListItem } from "@livechat/design-system-react-components";

import { NotTaggedFilterId } from "constants/filters";
import { IAgent } from "store/entities/agents";
import { ITag, TagAuthor } from "store/entities/tags";
import { ITeam } from "store/entities/teams";

import { alphabeticalSort } from "./sort";

export function mapTags(tags: ITag[], usageAllowed = true): ITag[] {
  if (!usageAllowed) {
    return [];
  }

  return tags;
}

export function getFilteredTags(tags: ITag[], tagIds: string[]): ITag[] {
  return tags.filter(tag => tagIds.includes(tag.id));
}

export function tagWasUpdated(tag: ITag): boolean {
  return !isEqual(tag.createdAt, tag.updatedAt);
}

export function getTagAuthorType(tag: ITag): TagAuthor {
  if (tagWasUpdated(tag)) {
    return "agent";
  }

  return tag.createdByType;
}

export function getTagAuthorName(tag: ITag, agents: IAgent[]): string | null {
  const agentId = tag.updatedBy || tag.createdBy;
  const agent = agents.find(agentItem => agentItem.id === agentId);
  return agent ? agent.name : null;
}

export const tagExists = (tagId: string, tags: ITag[]): boolean => {
  return tags?.some(t => t.id === tagId);
};

export const mapTagsToOptionsWithGrouping = (
  tags: ITag[],
  teams: ITeam[],
  currentTeamID?: string
): IPickerListItem[] => {
  const assignedTeam = teams.find(team => team.id === currentTeamID);
  const assignedTeamTags = tags.filter(tag => tag.teamId === assignedTeam?.id);

  const teamNames = Object.fromEntries(teams.map(team => [team.id, team.name]));

  const untaggedOption = tags.find(tag => tag.id === NotTaggedFilterId)
    ? [{ key: NotTaggedFilterId, name: "Not tagged" }]
    : [];

  const otherTeamTags = Object.fromEntries(
    teams
      .filter(team => team.id !== currentTeamID && team.id)
      .filter(team => tags.some(tag => tag.teamId === team.id))
      .map(team => [team.id, tags.filter(tag => tag.teamId === team.id)])
  );

  const globalTags = tags.filter(tag => !tag.teamId);

  const thisTeamOptions =
    assignedTeam && assignedTeamTags.length
      ? [
          {
            key: assignedTeam.id,
            name: `${assignedTeam.name} (Assigned)`,
            groupHeader: true,
          },
          ...assignedTeamTags.map(tag => ({ key: tag.id, name: tag.name })),
        ]
      : [];

  const globalOptions = globalTags.length
    ? [
        { key: "all-teams", name: "All teams", groupHeader: true },
        ...globalTags.map(tag => ({ key: tag.id, name: tag.name })),
      ]
    : [];

  const otherTeamOptions = [
    ...Object.entries(otherTeamTags)
      .filter(([teamId]) => teams.some(team => team.id === teamId))
      .sort(([teamIdA], [teamIdB]) => alphabeticalSort(teamNames[teamIdA], teamNames[teamIdB]))
      .flatMap(([teamId, tags]) => [
        { key: teamId, name: teamNames[teamId], groupHeader: true },
        ...tags.map(tag => ({ key: tag.id, name: tag.name })),
      ]),
  ];

  return [...untaggedOption, ...thisTeamOptions, ...globalOptions, ...otherTeamOptions];
};
